import type { EphemeralBranch } from '@core/store/SuperHub/Branches/utils';
import type { CreateBranchType, VersionRepresentationType } from '@readme/api-contracts/schemas/version';
import type { ReactNode } from 'react';

import { useContext, useEffect, useMemo, createContext } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';

import { useSuperHubStore } from '@core/store';

interface CreateBranchFormProviderProps {
  children: ReactNode;
}

/**
 * Creates a new React Hook Form that loads the initial state for creating a new
 * branch, and makes that state available to all descendants.
 */
export function CreateBranchFormProvider({ children }: CreateBranchFormProviderProps) {
  const ephemeralBranch = useSuperHubStore(s => s.branches.ephemeralBranch);

  const defaultBranch = useMemo(() => {
    if (!ephemeralBranch) return undefined;
    // `source` tracks the UI trigger location, not the API's branch source — exclude it from form defaults
    const { source, ...rest } = ephemeralBranch;
    return rest;
  }, [ephemeralBranch]);

  const formValue = useForm<CreateBranchType>({ defaultValues: defaultBranch });

  useEffect(() => {
    formValue.reset(defaultBranch);
  }, [defaultBranch, formValue]);

  return <FormProvider {...formValue}>{children}</FormProvider>;
}

/**
 * Returns the React Hook Form context to be used when creating branches.
 * @link https://react-hook-form.com/docs/useformcontext
 */
export const useCreateBranchFormContext = () => {
  const value = useFormContext<CreateBranchType>();
  if (!value) throw new Error('CreateBranchFormProvider is not provided');
  return value;
};

interface CreateBranchMetaContextValue {
  source: EphemeralBranch['source'];
  version: VersionRepresentationType;
}

const CreateBranchMetaContext = createContext<CreateBranchMetaContextValue | null>(null);

/**
 * Creates a new context that stores metadata about the current creation flow.
 */
export function CreateBranchMetaProvider({
  children,
  source,
  version,
}: CreateBranchMetaContextValue & { children: ReactNode }) {
  const memoizedValue = useMemo(
    () => ({
      source,
      version,
    }),
    [source, version],
  );

  return <CreateBranchMetaContext.Provider value={memoizedValue}>{children}</CreateBranchMetaContext.Provider>;
}

/**
 * Returns metadata about the current creation flow, like where it was initiated
 * and what the parent version is.
 */
export const useCreateBranchMetaContext = () => {
  const value = useContext(CreateBranchMetaContext);
  if (!value) throw new Error('CreateBranchMetaProvider is not provided');
  return value;
};
